import axios, { type AxiosInstance } from "axios";

import { defineStore } from "@/lib/tellscale/pinia.ts";

import { setupAuthInterceptors } from "@/lib/tellscale/interceptors";

import type { AppEnvironment } from "@/stores/app";
import type { ApiServiceUrls, ApiService, ApiHealthCheckEndpoints } from "@/stores/api";

/**
 * Store managing and initializing axios api instances for all services
 */
export const useApi = defineStore('api', () => {
    const app = shallowRef<AxiosInstance>(axios);
    const pub = shallowRef<AxiosInstance>(axios);

    const axiosInstances = computed(() => ({
        app: app.value,
        public: pub.value,
    }));

    /**
     * Get the API URLs based on the current environment
     *
     * @returns {string} Base URL for API requests
     */
    const getApiUrls = (environment: AppEnvironment): ApiServiceUrls => {
        switch(environment) {
            case 'development': return {
                app: 'http://localhost:8000',
                public: 'http://localhost:8004',
            };

            case 'staging': return {
                app: 'https://api.staging.tellscale.de/',
                public: 'https://api.public.staging.tellscale.de/',
            };

            case "production": return {
                app: 'https://api.tellscale.de/',
                public: 'https://api.public.tellscale.de/',
            };

            default: return {
                app: 'http://localhost:8000',
                public: 'http://localhost:8004',
            };
        }
    }

    /**
     * Get the API health check endpoints
     *
     * @returns {ApiHealthCheckEndpoints} API health check endpoints
     */
    const getApiHealthCheckEnpoints = (): ApiHealthCheckEndpoints => ({
        app: 'api/check',
        public: 'up',
    });

    /**
     * Configure Axios defaults with environment-specific base URL
     */
    const initAxios = async (environment: AppEnvironment): Promise<void> => {
        console.info(`[Api] Initializing axios for environment: ${environment}`);

        const apiUrls = getApiUrls(environment);

        app.value = axios.create({ baseURL: apiUrls.app, withCredentials: true });

        axios.defaults.baseURL = apiUrls.public;
        axios.defaults.withCredentials = true;

        pub.value = axios;

        setupAuthInterceptors(app.value);
        setupAuthInterceptors(pub.value);

        await checkServiceHealth('public');
        await checkServiceHealth('app');
    }

    /**
     * Check the health of a specific service
     *
     * @param {ApiService} service
     */
    const checkServiceHealth = async (service: ApiService) => {
        const axiosInstance = axiosInstances.value[service];

        const healthCheckEndpoints = getApiHealthCheckEnpoints();

        try {
            await axiosInstance.head(healthCheckEndpoints[service]);

            console.info(`[Api] Health Check for service "${service}" successful`);
        }
        catch(error) {
            console.error(`[Api] Health Check for service "${service}" failed`, error);
        }
    }

    /**
     * Reset all API instances to their initial state
     */
    const reset = (): void => {
        app.value = axios;
        pub.value = axios;
    }

    return { app, public: pub, initAxios, getApiUrls, reset, checkServiceHealth };
});
